import classNames from "classnames"
import { cloneElement, forwardRef } from "react"
import { Icon } from "../Icon"
import { FOCUS_STATE_BASE_CLASSES, FORM_COMPONENT_FOCUS_CLASSES } from "../utils/focusStateClasses"
import { isFormLabelComponent } from "../utils/isFormLabelComponent"
import type { SwitchProps } from "./types"

const SwitchInternalSlider = ({ checked, disabled }: Pick<SwitchProps, "checked" | "disabled">) => {
  const _classNameSwitchInternalSlider = classNames(
    "block relative w-5 h-5 rounded-full transition-transform duration-200",
    {
      "-rotate-180": !checked,
      "translate-x-6 rotate-0": checked,
      "bg-gray-300": disabled,
      "bg-white": !disabled,
    },
  )

  const _classNameIcon = classNames("absolute inset-0 m-auto transition-color duration-200", {
    "text-primary-base": checked && !disabled,
    "text-white": !checked && !disabled,
    "text-gray-300": !checked && disabled,
    "text-gray-200": checked && disabled,
  })

  return (
    <span className={_classNameSwitchInternalSlider}>
      <Icon className={_classNameIcon} name="Check" />
    </span>
  )
}

export const Switch = forwardRef<HTMLInputElement, SwitchProps>(
  ({ className, checked, disabled, label, id, name, ...rest }, ref) => {
    const _classNameSwitch = classNames(
      "min-w-[3.25rem] h-[1.75rem] rounded-full border-2 p-0.5 transition-all cursor-pointer",
      FORM_COMPONENT_FOCUS_CLASSES,
      FOCUS_STATE_BASE_CLASSES,
      {
        "bg-gray-300 border-gray-300": !checked && !disabled,
        "bg-primary-base border-primary-base": checked && !disabled,
        "bg-gray-200 border-gray-300": disabled,
        "hover:cursor-not-allowed": disabled,
      },
    )

    const _classNameContainer = classNames("flex justify-between gap-2", className)

    const switchProps = {
      checked,
      disabled,
      id: id || name,
      name,
      ref,
      role: "switch",
      ...rest,
    }

    /**
     * As FormLabel is polymorphic component we can render it as a div tag.
     * We need to this because we need to wrap the checkbox with the label and it is not possible
     * to have 2 labels associated with the same input.
     */
    const formLabelAsDiv = isFormLabelComponent(label)
      ? cloneElement(label, {
          ...label.props,
          as: "div",
          className: classNames(label.props.className, "cursor-pointer"),
        })
      : label

    return (
      <label htmlFor={id || name} className={_classNameContainer}>
        {formLabelAsDiv}
        <input type="checkbox" className="peer sr-only" {...switchProps} />
        <div className={_classNameSwitch}>
          <SwitchInternalSlider checked={checked} disabled={disabled} />
        </div>
      </label>
    )
  },
)
